import { z } from "zod";
import type { Timezone } from "../date-time/timezone";
import { timezoneZodType } from "../date-time/timezone";
import { defaultTimeZone } from "./default-time-zone";

type DailyScheduleTimeRange = {
    timeZone: Timezone;
    start: string; // e.g "10:30"
    startInMinutes: number;
    end: string; // e.g "10:30"
    endInMinutes: number;
    durationInMinutes: number;
};

const dailyScheduleTimeRangeZodSchema: z.ZodSchema<DailyScheduleTimeRange> =
    z.object({
        timeZone: timezoneZodType,
        start: z.string().regex(/^\d{2}:\d{2}$/),
        startInMinutes: z.number(),
        end: z.string().regex(/^\d{2}:\d{2}$/),
        endInMinutes: z.number(),
        durationInMinutes: z.number(),
    });

const dailyScheduleTimeRangeFactory = (
    start: { hour: number; minute: number },
    end: { hour: number; minute: number },
    timeZone = defaultTimeZone,
    allowIncorrectTime = true,
): DailyScheduleTimeRange[] => {
    const startHour = start.hour;
    const startMinute = isNaN(start.minute) ? 0 : start.minute;

    const endHour = end.hour;
    const endMinute = isNaN(end.minute) ? 0 : end.minute;

    if (
        (!allowIncorrectTime && endHour < startHour) ||
        (endHour === startHour && endMinute < start.minute)
    ) {
        // time reaches over midnight
        const firstDurationInMinutes =
            (23 - startHour) * 60 + (60 - startMinute);
        const secondDurationInMinutes = endHour * 60 + endMinute;
        const durationInMinutes =
            firstDurationInMinutes + secondDurationInMinutes;
        const result: DailyScheduleTimeRange[] = [
            {
                timeZone,
                start: `${startHour.toString().padStart(2, "0")}:${startMinute
                    .toString()
                    .padStart(2, "0")}`,
                startInMinutes: startHour * 60 + startMinute,
                end: "23:59",
                endInMinutes: 23 * 60 + 59,
                durationInMinutes,
            },
            {
                timeZone,
                start: "00:00",
                startInMinutes: 0,
                end: `${endHour.toString().padStart(2, "0")}:${endMinute
                    .toString()
                    .padStart(2, "0")}`,
                endInMinutes: endHour * 60 + endMinute,
                durationInMinutes,
            },
        ];
        return result;
    }

    const durationInMinutes =
        endHour * 60 + endMinute - startHour * 60 - startMinute;

    const result: DailyScheduleTimeRange[] = [
        {
            timeZone,
            start: `${startHour.toString().padStart(2, "0")}:${startMinute
                .toString()
                .padStart(2, "0")}`,
            startInMinutes: startHour * 60 + startMinute,
            end: `${endHour.toString().padStart(2, "0")}:${endMinute
                .toString()
                .padStart(2, "0")}`,
            endInMinutes: endHour * 60 + endMinute,
            durationInMinutes,
        },
    ];
    return result;
};

export { dailyScheduleTimeRangeFactory, dailyScheduleTimeRangeZodSchema };
export type { DailyScheduleTimeRange };
